/**
 * Org Settings API hooks — BOLT-081 + BOLT-076.
 *
 * React Query hooks for the org-settings endpoints:
 *   - GET   /v1/orgs/me      — current org info (organization + business + location)
 *   - PATCH /v1/orgs/me      — partial update of any of the three sections
 *   - GET   /v1/orgs/:id     — full org record including settings JSONB + version (BOLT-076)
 *   - PUT   /v1/orgs/:id     — merge-patch settings + optimistic concurrency (BOLT-076)
 *
 * The portal sends a nested `{ org?, business?, location? }` body (matches the
 * shared Zod schema). The form layer flattens these into a single set of
 * inputs for display and re-nests on submit.
 *
 * Cache invalidation: a successful PATCH invalidates ['orgSettings'] so the
 * page re-reads the canonical values returned by the server (including any
 * server-side normalization like phone formatting to E.164).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type OrgFullResponse, type PageToggles } from '@crm98/shared';
import type { Navigation } from '@membersolutionsinc/lighthouse/schemas';
import { apiClient } from '@/lib/api-client.js';

// ---------------------------------------------------------------------------
// Response / payload shapes (mirror BOLT-081 product-output.md §4)
// ---------------------------------------------------------------------------

/** Organization-level fields editable from this page. */
export interface OrgInfo {
  id: string;
  name: string;
}

/** Business (legal entity) fields editable from this page. */
export interface BusinessInfo {
  id: string;
  name: string;
  phone: string | null;
  billing_email: string | null;
}

/** Primary location (mailing address) fields editable from this page. */
export interface LocationInfo {
  id: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string;
  country: string;
}

/**
 * Response shape for both GET and PATCH /v1/orgs/me. `business` and `location`
 * may be null for newly created orgs that have not yet had those rows
 * provisioned — the page handles this by showing blank fields.
 */
export interface OrgSettingsResponse {
  org: OrgInfo;
  business: BusinessInfo | null;
  location: LocationInfo | null;
}

/**
 * PATCH body shape. Every top-level section is optional; only sections whose
 * fields the user actually changed are included (sparse update). The server
 * requires at least one section to be present.
 */
export interface PatchOrgSettingsInput {
  org?: {
    name?: string;
  };
  business?: {
    name?: string;
    phone?: string | null;
    billing_email?: string | null;
  };
  location?: {
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string;
    country?: string;
  };
}

// ---------------------------------------------------------------------------
// BOLT-076: Full org response (GET/PUT /v1/orgs/:id)
// ---------------------------------------------------------------------------

// OrgFullResponse is the canonical wire-format type defined in @crm98/shared.
// Re-export it here so portal modules can import from a single location.
export type { OrgFullResponse } from '@crm98/shared';

/**
 * Portal-local settings payload type used by the buildSettings callbacks in
 * the three settings cards. Uses plain string for font fields (the form layer
 * works with string values; the API Zod schema validates they're valid enum
 * members). Intentionally looser than the strict OrgSettings Zod-inferred
 * type in @crm98/shared to avoid enum casts in the build functions.
 */
export interface OrgFullSettings {
  design?: {
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string | null;
    logo_url?: string | null;
    heading_font?: string | null;
    body_font?: string | null;
    // BOLT-385: per-surface overrides. A null surface clears the override
    // (RFC 7396 deletes the key) and the site derives from brand again.
    surfaces?: {
      nav?: { background: string } | null;
      footer?: { background: string } | null;
      button?: { background: string } | null;
    };
  };
  tracking?: {
    ga4_measurement_id?: string | null;
    fb_pixel_id?: string | null;
    gtm_container_id?: string | null;
  };
  // BOLT-311: content section settings
  about?: {
    story?: string | null;
    mission?: string | null;
    values?: Array<{ title: string; description?: string }>;
    stats?: Array<{ label: string; value: string }>;
  };
  faq?: Array<{
    question: string;
    answer: string;
    display_order: number;
  }>;
  schedule?: {
    mode: 'entries' | 'pdf' | 'image';
    entries?: Array<{
      day_of_week: string;
      start_time: string;
      end_time: string;
      class_name: string;
      instructor_name?: string;
      level?: string;
      location_name?: string;
    }>;
    pdf_url?: string;
    image_url?: string;
    // BOLT-389: rich text beneath the schedule; explicit null clears (merge-patch)
    body?: string | null;
  };
  // Single-sourced from the SDK's PageTogglesSchema so a new toggle only needs
  // to be added there (not hand-mirrored here). Partial: stored settings may
  // predate any given toggle.
  page_toggles?: Partial<PageToggles>;
  // BOLT-424: the studio's own site menu — written whole, or null to go back
  // to the default menu. Page on/off stays in page_toggles.
  navigation?: Navigation | null;
  // BOLT-338: social profile links for website footer. BOLT-376 added the last three.
  social_links?: {
    instagram?: string | null;
    facebook?: string | null;
    youtube?: string | null;
    tiktok?: string | null;
    x?: string | null;
    google?: string | null;
    yelp?: string | null;
  };
  // Lead-form + site-wide display options. show_hours_on_forms is the
  // site-wide hours visibility switch (forms, location pages, JSON-LD). It does
  // NOT reach the site footer: Beam #219 removed the footer hours column
  // outright, so there is no footer hours to toggle.
  contact_form?: {
    require_phone?: boolean;
    show_hours_on_forms?: boolean;
  };
  // CTA Banner section settings
  cta_banner?: {
    variant: 'full_width' | 'card_background';
    headline: string;
    body?: string;
    button_text: string;
    button_url: string;
    background_image_url?: string | null;
    use_gradient?: boolean;
  };
  // BOLT-312: hero section settings
  hero?: {
    variant: 'centered' | 'split_image' | 'video_background';
    headline: string;
    subheadline?: string;
    cta_text: string;
    cta_url: string;
    background_image_url?: string | null;
    video_url?: string | null;
    poster_url?: string | null;
    overlay_opacity?: number;
  };
  // BOLT-397: authored per-page SEO, keyed by canonical route. RFC 7396:
  // null clears a field (reset to derived); a null page entry removes the
  // route's authoring entirely.
  seo?: {
    pages?: Record<string, { title?: string | null; description?: string | null } | null>;
  };
}

/** Request body for PUT /v1/orgs/:id. */
export interface PutOrgBody {
  name?: string;
  version: number;
  settings?: OrgFullSettings;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const orgSettingsQueryKeys = {
  orgSettings: ['orgSettings'] as const,
  orgFull: (orgId: string) => ['orgFull', orgId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated org's settings (org, business, location).
 * Used by OrgSettingsPage to populate the form.
 */
export function useOrgSettings() {
  return useQuery<OrgSettingsResponse>({
    queryKey: orgSettingsQueryKeys.orgSettings,
    queryFn: () => apiClient.get<OrgSettingsResponse>('/v1/orgs/me'),
    // Settings data is stable enough that window-focus refetching just adds
    // noise; the conflict-dialog path handles actual concurrency.
    refetchOnWindowFocus: false,
    staleTime: 300_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Mutation for PATCH /v1/orgs/me. Invalidates ['orgSettings'] on success so
 * the page reflects server-normalized values (e.g. phone in E.164 format).
 *
 * Server error codes the form maps to field-level errors via ApiError.details:
 *   - 400 validation_error → details[] mapped to RHF setError per field
 *   - 403 forbidden        → root error (staff role attempting to save)
 *   - 404 not_found        → root error (org missing)
 */
export function useUpdateOrgSettings() {
  const queryClient = useQueryClient();
  return useMutation<OrgSettingsResponse, Error, PatchOrgSettingsInput>({
    mutationFn: (body) => apiClient.patch<OrgSettingsResponse>('/v1/orgs/me', { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgSettingsQueryKeys.orgSettings });
    },
  });
}

// ---------------------------------------------------------------------------
// BOLT-076: Full org settings (GET/PUT /v1/orgs/:id)
// ---------------------------------------------------------------------------

/**
 * Fetches the full org record including settings JSONB and version.
 * Used by the settings cards (Brand & Design, Tracking, Navigation, Site Options).
 */
export function useOrgFullSettings(orgId: string, enabled = true) {
  return useQuery<OrgFullResponse>({
    queryKey: orgSettingsQueryKeys.orgFull(orgId),
    queryFn: () => apiClient.get<OrgFullResponse>(`/v1/orgs/${orgId}`),
    enabled: enabled && !!orgId,
    refetchOnWindowFocus: false,
    staleTime: 300_000,
  });
}

/**
 * Mutation for PUT /v1/orgs/:id. Applies RFC 7396 merge patch to org settings.
 * Invalidates the orgFull query on success so all three cards re-render with
 * the updated version number.
 *
 * The caller is responsible for passing the current `version` in the body.
 * A 409 version_conflict should be surfaced to the user via ConflictDialog.
 */
export function useUpdateOrgFullSettings(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<OrgFullResponse, Error, PutOrgBody>({
    mutationFn: (body) => apiClient.put<OrgFullResponse>(`/v1/orgs/${orgId}`, { body }),
    onSuccess: (data) => {
      // Seed the cache immediately so sibling cards read the new version number
      // before any background refetch completes — prevents spurious 409s when
      // two cards are saved in quick succession.
      queryClient.setQueryData(orgSettingsQueryKeys.orgFull(orgId), data);
    },
  });
}
